import { useEffect, useState } from "react";

/**
 * Countdown timer persisted via an end-timestamp in localStorage
 * (same pattern as the TFA resend cooldown).
 *
 * @param secondsWhenMissing - value used on mount when no end timestamp is stored
 *   (TFA uses the cooldown duration; password-forgotten uses 0 so submit stays enabled)
 */
const useLocalStorageCountdown = (
    storageKey: string,
    secondsWhenMissing = 0
): {
    secondsRemaining: number;
    start: (durationSeconds: number) => void;
    clear: () => void;
} => {
    const getInitialTimerValue = () => {
        const savedTimerEnd = localStorage.getItem(storageKey);

        if (savedTimerEnd) {
            return Math.max(
                0,
                Math.ceil((parseInt(savedTimerEnd, 10) - Date.now()) / 1000)
            );
        }

        return secondsWhenMissing;
    };

    const [secondsRemaining, setSecondsRemaining] = useState(
        getInitialTimerValue
    );

    useEffect(() => {
        if (secondsRemaining <= 0) {
            return;
        }

        if (!localStorage.getItem(storageKey)) {
            localStorage.setItem(
                storageKey,
                (Date.now() + secondsRemaining * 1000).toString()
            );
        }

        const intervalId = setInterval(() => {
            setSecondsRemaining((prev) => {
                const newTime = prev - 1;

                if (newTime <= 0) {
                    clearInterval(intervalId);
                    localStorage.removeItem(storageKey);
                    return 0;
                }

                return newTime;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [secondsRemaining, storageKey]);

    const start = (durationSeconds: number) => {
        const duration = Math.max(1, Math.ceil(durationSeconds));
        localStorage.setItem(
            storageKey,
            (Date.now() + duration * 1000).toString()
        );
        setSecondsRemaining(duration);
    };

    const clear = () => {
        localStorage.removeItem(storageKey);
        setSecondsRemaining(0);
    };

    return { secondsRemaining, start, clear };
};

export default useLocalStorageCountdown;
